import { ListingStatus, recursiveToString } from '@apeiron/common'
import { PlanetFilterInput } from '@apeiron/planet'
import { Nullable } from '@apeiron/temp'
import * as R from 'ramda'

const toArray = <T>(value: T[]): T[] | undefined => {
  return Array.isArray(value) ? value : [value]
}

export const initGraphqlFilter = (
  input: Nullable<PlanetFilterInput>
): PlanetFilterInput => {
  const filter: PlanetFilterInput = {
    activeCSkillCountMax: input?.activeCSkillCountMax ?? undefined,
    activeCSkillCountMin: input?.activeCSkillCountMin ?? undefined,
    ageMax: input.ageMax ?? undefined,
    ageMin: input.ageMin ?? undefined,
    airMax: input.airMax ?? undefined,
    airMin: input.airMin ?? undefined,
    armorMax: input.armorMax ?? undefined,
    armorMin: input.armorMin ?? undefined,
    atkPowerMax: input.atkPowerMax ?? undefined,
    atkPowerMin: input.atkPowerMin ?? undefined,
    atkRangeMax: input.atkRangeMax ?? undefined,
    atkRangeMin: input.atkRangeMin ?? undefined,
    atkSpeedMax: input.atkSpeedMax ?? undefined,
    atkSpeedMin: input.atkSpeedMin ?? undefined,
    breedCountMax: input.breedCountMax ?? undefined,
    breedCountMin: input.breedCountMin ?? undefined,
    breedCountMaxMax: input.breedCountMaxMax ?? undefined,
    breedCountMaxMin: input.breedCountMaxMin ?? undefined,
    coreType: !R.isNil(input.coreType)
      ? toArray<number>(input.coreType)
      : undefined,
    critChanceMax: input.critChanceMax ?? undefined,
    critChanceMin: input.critChanceMin ?? undefined,
    critDmgMax: input.critDmgMax ?? undefined,
    critDmgMin: input.critDmgMin ?? undefined,
    cSkillID: !R.isNil(input.cSkillID)
      ? toArray<number>(input.cSkillID)
      : undefined,
    earthMax: input.earthMax ?? undefined,
    earthMin: input.earthMin ?? undefined,
    energyMax: input.energyMax ?? undefined,
    energyMin: input.energyMin ?? undefined,
    evolveMax: input.evolveMax ?? undefined,
    evolveMin: input.evolveMin ?? undefined,
    fireMax: input.fireMax ?? undefined,
    fireMin: input.fireMin ?? undefined,
    gender: input.gender ?? undefined,
    healthMax: input.healthMax ?? undefined,
    healthMin: input.healthMin ?? undefined,
    nftListingStatus: !R.isNil(input.nftListingStatus)
      ? toArray<ListingStatus>(input.nftListingStatus)
      : undefined,
    ownerWalletAddresses: input.ownerWalletAddresses ?? undefined,
    planetClassID: !R.isNil(input.planetClassID)
      ? toArray<number>(input.planetClassID)
      : undefined,
    planetID: !R.isNil(input.planetID)
      ? recursiveToString(toArray<string>(input.planetID) as string[])
      : undefined,
    planetType: !R.isNil(input.planetType)
      ? toArray<string>(input.planetType)
      : undefined,
    primevalLegacyMax: !R.isNil(input.primevalLegacyMax)
      ? input.primevalLegacyMax
      : undefined,
    primevalLegacyMin: input.primevalLegacyMin ?? undefined,
    pSkillID: !R.isNil(input.pSkillID)
      ? toArray<number>(input.pSkillID)
      : undefined,
    specialGeneMax: input.specialGeneMax ?? undefined,
    specialGeneMin: input.specialGeneMin ?? undefined,
    stage: !R.isNil(input.stage) ? toArray<string>(input.stage) : undefined,
    waterMax: input.waterMax ?? undefined,
    waterMin: input.waterMin ?? undefined,
    cSkillReqElem: input.cSkillReqElem ?? undefined,
    pSkillReqElem: input.pSkillReqElem ?? undefined
  }

  return filter as PlanetFilterInput
}

export const toLayoutFilter = (opts: PlanetFilterInput): PlanetFilterInput => {
  const filterOption: PlanetFilterInput = {
    activeCSkillCountMax: opts.activeCSkillCountMax ?? undefined,
    activeCSkillCountMin: opts.activeCSkillCountMin ?? undefined,
    ageMax: opts.ageMax ?? undefined,
    ageMin: opts.ageMin ?? undefined,
    airMax: opts.airMax ?? undefined,
    airMin: opts.airMin ?? undefined,
    armorMax: opts.armorMax ?? undefined,
    armorMin: opts.armorMin ?? undefined,
    atkPowerMax: opts.atkPowerMax ?? undefined,
    atkPowerMin: opts.atkPowerMin ?? undefined,
    atkRangeMax: opts.atkRangeMax ?? undefined,
    atkRangeMin: opts.atkRangeMin ?? undefined,
    atkSpeedMax: opts.atkSpeedMax ?? undefined,
    atkSpeedMin: opts.atkSpeedMin ?? undefined,
    breedCountMax: opts.breedCountMax ?? undefined,
    breedCountMin: opts.breedCountMin ?? undefined,
    coreType: opts.coreType ?? undefined,
    critChanceMax: opts.critChanceMax ?? undefined,
    critChanceMin: opts.critChanceMin ?? undefined,
    critDmgMax: opts.critDmgMax ?? undefined,
    critDmgMin: opts.critDmgMin ?? undefined,
    cSkillID: opts.cSkillID ?? undefined,
    earthMax: opts.earthMax ?? undefined,
    earthMin: opts.earthMin ?? undefined,
    energyMax: opts.energyMax ?? undefined,
    energyMin: opts.energyMin ?? undefined,
    fireMax: opts.fireMax ?? undefined,
    fireMin: opts.fireMin ?? undefined,
    healthMax: opts.healthMax ?? undefined,
    healthMin: opts.healthMin ?? undefined,
    nftListingStatus: opts.nftListingStatus ?? undefined,
    ownerWalletAddresses: opts.ownerWalletAddresses ?? undefined,
    planetClassID: opts.planetClassID ?? undefined,
    planetType: opts.planetType ?? undefined,
    planetID: opts.planetID ?? undefined,
    primevalLegacyMax: opts.primevalLegacyMax ?? undefined,
    primevalLegacyMin: opts.primevalLegacyMin ?? undefined,
    pSkillID: opts.pSkillID ?? undefined,
    stage: opts.stage ?? undefined,
    waterMax: opts.waterMax ?? undefined,
    waterMin: opts.waterMin ?? undefined,
    cSkillReqElem: opts.cSkillReqElem ?? undefined,
    pSkillReqElem: opts.pSkillReqElem ?? undefined
  }

  return filterOption as PlanetFilterInput
}

export const toGraphqlFilter = (opts: PlanetFilterInput): PlanetFilterInput => {
  const filterInput: PlanetFilterInput = {
    activeCSkillCountMax: opts.activeCSkillCountMax ?? undefined,
    activeCSkillCountMin: opts.activeCSkillCountMin ?? undefined,
    ageMax: opts.ageMax ?? undefined,
    ageMin: opts.ageMin ?? undefined,
    airMax: opts.airMax ?? undefined,
    airMin: opts.airMin ?? undefined,
    armorMax: opts.armorMax ?? undefined,
    armorMin: opts.armorMin ?? undefined,
    atkPowerMax: opts.atkPowerMax ?? undefined,
    atkPowerMin: opts.atkPowerMin ?? undefined,
    atkRangeMax: opts.atkRangeMax ?? undefined,
    atkRangeMin: opts.atkRangeMin ?? undefined,
    atkSpeedMax: opts.atkSpeedMax ?? undefined,
    atkSpeedMin: opts.atkSpeedMin ?? undefined,
    breedCountMax: opts.breedCountMax ?? undefined,
    breedCountMin: opts.breedCountMin ?? undefined,
    breedCountMaxMax: undefined, // no filter on layout
    breedCountMaxMin: undefined, // no filter on layout
    coreType: opts.coreType ?? undefined,
    critChanceMax: opts.critChanceMax ?? undefined,
    critChanceMin: opts.critChanceMin ?? undefined,
    critDmgMax: opts.critDmgMax ?? undefined,
    critDmgMin: opts.critDmgMin ?? undefined,
    cSkillID: opts.cSkillID ?? undefined,
    earthMax: opts.earthMax ?? undefined,
    earthMin: opts.earthMin ?? undefined,
    energyMax: opts.energyMax ?? undefined,
    energyMin: opts.energyMin ?? undefined,
    evolveMax: undefined, // no filter on layout
    evolveMin: undefined, // no filter on layout
    fireMax: opts.fireMax ?? undefined,
    fireMin: opts.fireMin ?? undefined,
    gender: undefined, // no filter on layout
    healthMax: opts.healthMax ?? undefined,
    healthMin: opts.healthMin ?? undefined,
    nftListingStatus: opts.nftListingStatus ?? undefined,
    ownerWalletAddresses: opts.ownerWalletAddresses ?? undefined,
    planetClassID: opts.planetClassID ?? undefined,
    planetID: !R.isNil(opts.planetID)
      ? recursiveToString(toArray<string>(opts.planetID) as string[])
      : undefined, // no filter on layout, only support direct query
    planetType: opts.planetType ?? undefined,
    primevalLegacyMax: opts.primevalLegacyMax ?? undefined,
    primevalLegacyMin: opts.primevalLegacyMin ?? undefined,
    pSkillID: opts.pSkillID ?? undefined,
    specialGeneMax: undefined, // no filter on layout
    specialGeneMin: undefined, // no filter on layout
    stage: opts.stage ?? undefined,
    waterMax: opts.waterMax ?? undefined,
    waterMin: opts.waterMin ?? undefined,
    cSkillReqElem: opts.cSkillReqElem ?? undefined,
    pSkillReqElem: opts.pSkillReqElem ?? undefined
  }

  return filterInput as PlanetFilterInput
}

export const isFilterInputEmpty = (filterInput: PlanetFilterInput): boolean => {
  return R.isEmpty(R.reject(R.isNil, filterInput))
}
